import { uuid } from "@sinch/utils/crypto";
import { isNil } from "ramda";
import { useCallback } from "react";
import { useRateTreeContext } from "@sinch/components/application/RatelistBuilder/BuilderTree/RateBuilderTreeProvider";
import {
  rateItemSchema,
  RateItemSchemaFormFieldsInfer,
} from "@sinch/components/application/RatelistBuilder/ratelistBuilderSchema";
import { RateBuilderTreeViewItem } from "@sinch/components/application/RatelistBuilder/types";

export const useAddTreeNode = (nodeId: string | null = null, disableCondition?: boolean) => {
  const { add, expand, move, getParent, getIndex } = useRateTreeContext<
    RateBuilderTreeViewItem,
    RateItemSchemaFormFieldsInfer
  >();

  // @ts-ignore expected, the type is different from defaults
  const defaultItem = rateItemSchema(disableCondition).getDefault() as RateItemSchemaFormFieldsInfer;

  // Action handlers
  const addCondition = useCallback(() => {
    add(nodeId, { id: uuid(), label: uuid() }, defaultItem);
  }, [add, expand, nodeId]);

  const addIfElseNodes = useCallback(() => {
    const wrapperId = uuid();
    add(nodeId, {
      id: wrapperId,
      label: wrapperId,
      type: "wrapper",
    });
    add(
      wrapperId,
      {
        id: uuid(),
        label: uuid(),
      },
      defaultItem
    );
    add(
      wrapperId,
      {
        id: uuid(),
        label: uuid(),
      },
      defaultItem
    );
  }, [add, expand, nodeId]);

  // Action else node
  const addElseNode = useCallback(() => {
    if (!nodeId) {
      return;
    }
    const parent = getParent(nodeId);
    const itemParentId = parent?.id ?? null;
    const index = getIndex(nodeId);
    if (isNil(index)) {
      return;
    }
    if (parent?.type === "wrapper") {
      add(
        itemParentId,
        {
          id: uuid(),
          label: uuid(),
        },
        defaultItem
      );
    } else {
      const wrapperId = uuid();
      add(itemParentId, {
        id: wrapperId,
        label: wrapperId,
        type: "wrapper",
      });
      move(wrapperId, itemParentId, index);
      move(nodeId, wrapperId);
      add(
        wrapperId,
        {
          id: uuid(),
          label: uuid(),
        },
        defaultItem
      );
    }
  }, [add, expand, nodeId]);

  return { addCondition, addIfElseNodes, addElseNode };
};
